/**
 * ProjectEditPage Component
 *
 * Page for viewing and editing an existing project
 */

import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProject, useUpdateProject } from '../hooks/useProjects';
import { ProjectForm } from '../components/projects/ProjectForm';
import { ProjectFormData } from '../utils/validation';
import toast from 'react-hot-toast';

export const ProjectEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: project, isLoading, error } = useProject(id);
  const updateMutation = useUpdateProject();

  const handleSubmit = async (data: ProjectFormData) => {
    if (!id) return;

    try {
      await updateMutation.mutateAsync({ id, data });
      toast.success('Project updated successfully');
      navigate('/projects');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update project');
    }
  };

  const handleCancel = () => {
    navigate('/projects');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-3xl font-bold text-gray-900">Edit Project</h1>
          <p className="mt-1 text-sm text-gray-500">
            Update your web agent project template
          </p>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center items-center py-12">
            <svg
              className="animate-spin h-8 w-8 text-blue-600"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Error loading project
                </h3>
                <p className="mt-1 text-sm text-red-700">{error.message}</p>
                <button
                  onClick={() => navigate('/projects')}
                  className="mt-2 text-sm font-medium text-red-600 hover:text-red-500"
                >
                  Back to projects
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        {!isLoading && !error && project && (
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <ProjectForm
              initialData={project}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              isLoading={updateMutation.isPending}
              submitLabel="Update Project"
            />
          </div>
        )}
      </main>
    </div>
  );
};
